<template>
  <v-card class="pa-2 mx-auto" max-width="800">
    <v-toolbar>
      <v-toolbar-title>
        Racer Adjustments
      </v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <v-container v-if="payout">
        <v-row>
          <v-col lg="8" md="8" sm="10" xs="12">
            <h2>Success!</h2>
            <p class="mt-3 mb-10">An Adjustment <b>payout</b> has been created.</p>
            <v-btn :loading="loading" color="primary" @click="onStartOver">
              Create Another Adjustment
            </v-btn>
          </v-col>
          <v-col cols="4">
            <ApprovalSidebar :payout="payout" :saleParticipant="selectedRacer" />
          </v-col>
        </v-row>
      </v-container>

      <v-container v-else>
        <ApiError :errors="errors"></ApiError>
        <p>
          Please use the following form to issue an Adjustment for a Racer. All required fields are indicated with a
          <span class="red--text">*</span>.
        </p>
        <v-form ref="adjustmentLookupForm" @submit.prevent="onSubmit" v-if="!loading" v-model="valid">
          <v-container>
            <v-row>
              <v-col cols="12">
                <ParticipantField
                  outlined
                  dense
                  showKey
                  :label="'Select a ' + $i18n.translate('Racer')"
                  v-model="selectedRacer"
                  :rules="rules.participant"
                  class="required"
                  :participantType="{ participantTypeKey: 400 }"
                  showLegacyParticipantKey
                />
                <v-alert
                  v-if="selectedRacer && (!availableAwardVehicles || availableAwardVehicles.length === 0)"
                  type="error"
                  style="margin-top: -15px;"
                  dense
                  outlined
                >
                  No Award Vehicle is configured/available for this racer. Please configure an Award Vehicle for the
                  program before creating an Adjustment.
                </v-alert>
              </v-col>
              <v-col cols="12" sm="12">
                <div v-if="selectedRacer && vins">
                  <v-text-field
                    v-if="selectedRacer"
                    outlined
                    dense
                    ref="payoutAmount"
                    id="payoutAmount"
                    name="payoutAmount"
                    :label="'Enter an Adjustment ' + $i18n.translate('Amount') + ' ' + minimumAndMaximumsText()"
                    type="number"
                    v-model="form.payoutAmount"
                    :min="selectedAwardVehicle?.minimumPayoutAmount || 0"
                    :max="selectedAwardVehicle?.maximumPayoutAmount || 99999"
                    :rules="rules.payoutAmount"
                    :error-messages="$error.getValidationError(errors, 'payoutAmount')"
                    @input="$error.clearValidationError(errors, 'payoutAmount')"
                    :disabled="!selectedAwardVehicle"
                    :hint="!selectedAwardVehicle ? 'Need to define an award vehicle first.' : ''"
                    persistent-hint
                    class="required"
                  />

                  <v-select
                    outlined
                    dense
                    v-model="form.customFieldValue8"
                    :items="vins"
                    label="Search for VIN"
                    :rules="rules.vin"
                    v-if="selectedRacer && vins"
                    :disabled="!selectedRacer"
                    :hint="!selectedRacer ? 'Need to define an racer first.' : ''"
                    persistent-hint
                    class="required"
                  />
                  <v-alert
                    v-if="selectedRacer && (!vins || vins.length === 0)"
                    type="error"
                    style="margin-top: -10px;"
                    dense
                    outlined
                  >
                    This racer has no registered VINs. You cannot create an Adjustment until a VIN is registered for
                    this racer.
                  </v-alert>
                  <CustomField
                    v-for="(field, index) in visibleCustomFields"
                    :key="index"
                    outlined
                    dense
                    v-model="form[`customFieldValue${index + 1}`]"
                    :customField="field"
                    class="required"
                    required
                  />

                  <DateTimePickerField
                    dense
                    outlined
                    v-model="form.eventDate"
                    :label="$i18n.translate('Event Date')"
                    :class="form.eventDate ? 'required' : ''"
                    :error-messages="$error.getValidationError(errors, 'eventDate')"
                    @input="$error.clearValidationError(errors, 'eventDate')"
                    onlyDate
                    required
                    format="MM/DD/YYYY"
                    :rules="rules.eventDate"
                  />
                  <v-textarea
                    outlined
                    dense
                    :rows="isMobile ? 5 : 3"
                    name="comment"
                    label="Enter an Adjustment Message or Comment"
                    id="comment"
                    class="required"
                    v-model="form.comment"
                    :rules="rules.comment"
                    :counter="FIELD_MAX_LENGTHS.PAYOUT_MESSAGE"
                    :maxlength="FIELD_MAX_LENGTHS.PAYOUT_MESSAGE"
                  />
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn class="mt-3" :loading="loading" color="primary" :disabled="!valid" @click="onSubmit">
                  Submit
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import CustomField from "@/gapp-components/components/fields/CustomField.vue";
import DateTimePickerField from "@/gapp-components/components/fields/DateTimePickerField.vue";
import ParticipantField from "@/gapp-components/components/fields/ParticipantField.vue";
import { mapGetters } from "vuex";
import moment from "moment-timezone";
import ValidationService from "@/gapp-components/services/validation.service";
import ApprovalSidebar from "@/gapp-components/components/views/promotions/ApprovalSidebar.vue";

import FIELD_MAX_LENGTHS from "@/gapp-components/utils/fieldMaxLengths";

export default {
  components: {
    ApiError,
    ApprovalSidebar,
    CustomField,
    DateTimePickerField,
    ParticipantField
  },
  name: "RacerAdjustments",
  metaInfo: {
    title: "Racer Adjustments"
  },
  data: () => ({
    FIELD_MAX_LENGTHS,

    tab: null,
    step: "1",
    valid: false,
    loading: false,
    errors: {},
    assignmentValue: null,
    form: {
      eventDate: null,
      payoutAmount: 0,
      customFieldValue8: ""
    },
    vins: [],
    successMessage: "",
    errorMessage: "",
    selectedVin: null,
    selectedRacer: null,
    selectedAwardVehicle: null,
    availableAwardVehicles: [],
    payout: null
  }),
  created() {
    this.visibleCustomFields.forEach((field, index) => {
      this.$set(this.form, `customFieldValue${index + 1}`, null);
    });
  },
  computed: {
    ...mapGetters(["selectedClient", "selectedProgram"]),

    visibleCustomFields() {
      return this.selectedProgram?.payoutCustomFields?.slice(0, 7) || [];
    },

    rules() {
      return {
        assignment: [
          ValidationService.generateNotWhitespacesRule("assignment"),
          ...ValidationService.generateDropdownValidationRules("assignment")
        ],
        awardVehicle: ValidationService.generateDropdownValidationRules("award vehicle"),
        payoutAmount: [
          v => !!v || "A payout amount is required.",
          v =>
            v >= (this.selectedAwardVehicle?.minimumPayoutAmount || 0) ||
            `Amount must be at least ${this.selectedAwardVehicle?.minimumPayoutAmount}`,
          v =>
            v <= (this.selectedAwardVehicle?.maximumPayoutAmount || 99999) ||
            `Amount must be less than ${this.selectedAwardVehicle?.maximumPayoutAmount ?? 99999}`
        ],
        participant: ValidationService.generateDropdownValidationRules("Racer"),
        eventDate: [v => !!v || "Event Date is required."],
        comment: ValidationService.generateTextValidationRules("Message or Comment", 255),
        vin: [v => !!v || "VIN is required."]
      };
    }
  },
  methods: {
    async loadVinsForRacer(participant) {
      try {
        const response = await this.$api.post(
          "/api/productRegistrations/search?expand=productRegistrationItems,productRegistrationItems.product",
          {
            participant: { id: participant.id }
          }
        );
        const vins = response.data?.content || [];
        this.vins = vins.map(item => {
          const serialNumber = item?.productRegistrationItems?.[0]?.serialNumber || "Unknown VIN";
          return {
            value: serialNumber,
            text: serialNumber
          };
        });

        this.selectedVin = this.vins[0]?.value || null;
      } catch (error) {
        console.error("Error fetching VINs:", error);
        this.vins = [];
        this.selectedVin = null;
      }
    },
    minimumAndMaximumsText() {
      let text = "";
      if (this.selectedAwardVehicle) {
        if (this.selectedAwardVehicle.minimumPayoutAmount && this.selectedAwardVehicle.maximumPayoutAmount) {
          text =
            "(min: " +
            this.selectedAwardVehicle?.minimumPayoutAmount +
            ", max: " +
            this.selectedAwardVehicle.maximumPayoutAmount +
            ")";
        } else if (this.selectedAwardVehicle.minimumPayoutAmount) {
          text = "(min: " + this.selectedAwardVehicle.minimumPayoutAmount + ")";
        } else if (this.selectedAwardVehicle.maximumPayoutAmount) {
          text = "(max: " + this.selectedAwardVehicle.maximumPayoutAmount + ")";
        }
      }
      return text;
    },

    onStartOver() {
      this.payout = null;
      this.selectedAwardVehicle = null;
      this.selectedRacer = null;
      this.form = {
        payoutAmount: 0,
        comment: null
      };
    },

    onSubmit() {
      this.errors = {};
      this.loading = true;

      return this.createPayout()
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    createDuplicationKey() {
      //TG_2025_07_20T07_00_00_000_00_00_1471_3_3_3_3_3_3_3
      let duplicationKey = "TG";
      duplicationKey += "_" + this.form?.eventDate.replace(/[^a-zA-Z0-9]/g, "_");
      duplicationKey += `_${this.selectedRacer.id}`;
      this.visibleCustomFields.forEach((field, index) => {
        const key = `customFieldValue${index + 1}`;
        const value = this.form[key];
        console.log(value);
        if (value != null) {
          duplicationKey += "_" + value.replace(/[^a-zA-Z0-9]/g, "_");
        }
      });
      return duplicationKey;
    },
    formatDatetime(value) {
      return moment.tz(value, "YYYYMMDD", this.selectedClient.timezone).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
    },
    createPayout() {
      if (this.selectedAwardVehicle == null) {
        return Promise.reject("Unable to find racer award vehicle in system");
      }
      if (this.selectedRacer == null) {
        return Promise.reject("Assignment of adjustment is missing");
      }
      let payout = {
        duplicationKey: this.createDuplicationKey(),
        participant: { id: this.selectedRacer.id },
        payoutAmount: this.form.payoutAmount,
        awardVehicle: { id: this.selectedAwardVehicle.id },
        awardVehicleDetermination: { name: "DETERMINED_BY_MANUAL" },
        currency: { name: "USD" },
        payoutType: { name: "MONEY" },
        payoutCategory: "RACE_AWARD",
        payoutMessage: this.form.comment || "",
        customFieldValue8: this.form.customFieldValue8,
        effectiveDate: this.formatDatetime(this.form.eventDate)
      };
      let customPayout = {};
      this.visibleCustomFields.forEach((field, index) => {
        const key = `customFieldValue${index + 1}`;
        const value = this.form[key];
        if (value != null) {
          customPayout[key] = value;
        }
      });
      const finalPayout = { ...payout, ...customPayout };
      return this.$api
        .post("/api/payouts/create", finalPayout)
        .then(({ data }) => {
          this.payout = data;
          return this.payPayout(data);
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        });
    },

    payPayout(payout) {
      return this.$api.post("/api/payouts/" + payout.id + "/payPayouts", {}).then(() => {
        return Promise.resolve();
      });
    },

    fetchAvailableAwardVehicles() {
      this.selectedAwardVehicle = null;
      if (this.selectedRacer) {
        return this.$awardvehicle.getAvailableAwardVehicles(this.selectedRacer, null, null).then(avs => {
          this.availableAwardVehicles = avs;
          const racerVehicle = this.availableAwardVehicles.find(av => av?.awardVehicleKey === "RACER_PAYOUTS");
          this.selectedAwardVehicle = racerVehicle;
        });
      } else {
        return Promise.resolve();
      }
    }
  },
  watch: {
    selectedRacer(newVal) {
      this.form.customFieldValue8 = null;
      this.vins = [];
      if (newVal) {
        this.loadVinsForRacer(newVal);
        this.fetchAvailableAwardVehicles();
      }
    },
    selectedAwardVehicle(v) {
      if (v) {
        this.$nextTick(() => {
          this.$refs.payoutAmount.validate();
        });
      }
    },
    selectedVin(newVal) {
      this.visibleCustomFields.customFieldValue8 = newVal;
    },
    assignmentValue(newValue, oldValue) {
      if (newValue !== null && newValue !== oldValue) {
        this.onStartOver();
      }
    }
  }
};
</script>
